import { useState } from 'react';

import { csvFormatHint, customFieldHeader, withTemplateColumns, type CustomFieldTemplate } from './customFields';
import { useCustomFieldTemplates } from './useCustomFieldTemplates';

export type CustomFieldsChoice = 'yes' | 'no';

/**
 * Upload modal state: "Add custom fields?" (default Yes) and the chosen template.
 * Download Template / file upload stay off until a template is picked when Yes.
 */
export function useUploadCustomFields() {
  const { enabled, templates, loading } = useCustomFieldTemplates();
  const [choice, setChoice] = useState<CustomFieldsChoice>('yes');
  const [templateId, setTemplateId] = useState<number | null>(null);
  const template = enabled && choice === 'yes' ? templates.find((t) => t.id === templateId) || null : null;
  const ready = !enabled || choice === 'no' || !!template;

  const reset = () => {
    setChoice('yes');
    setTemplateId(null);
  };

  /** Columns + sample row for downloadTemplate(). */
  const templateColumns = (baseColumns: string[], baseSample: Record<string, string>[]) =>
    withTemplateColumns(baseColumns, baseSample, template);

  /** Adds custom_field_template_id to the upload FormData when a template is chosen. */
  const appendTo = (formData: FormData) => {
    if (template) formData.append('custom_field_template_id', String(template.id));
  };

  return {
    enabled,
    templates,
    loading,
    choice,
    setChoice,
    templateId,
    setTemplateId,
    template,
    ready,
    reset,
    templateColumns,
    appendTo,
  };
}

type UploadCustomFieldsState = ReturnType<typeof useUploadCustomFields>;

const inputCls = 'input-embossed h-9 w-full rounded-md px-2.5 text-sm disabled:opacity-50';

/** Renders nothing when the Contacts "Custom fields" ACL option is off. */
export function UploadCustomFieldsChooser({ state, disabled }: { state: UploadCustomFieldsState; disabled?: boolean }) {
  const { enabled, templates, loading, choice, setChoice, templateId, setTemplateId, template } = state;
  if (!enabled) return null;

  return (
    <div className="mb-3 space-y-3 rounded-xl border border-border/60 bg-muted/20 p-3">
      <div className="space-y-1.5">
        <span className="text-sm font-semibold text-foreground">Do you want to add custom fields?</span>
        <div className="flex gap-5">
          {(['yes', 'no'] as const).map((v) => (
            <label key={v} className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="upload-custom-fields"
                className="h-4 w-4 accent-primary"
                checked={choice === v}
                disabled={disabled}
                onChange={() => setChoice(v)}
              />
              {v === 'yes' ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      </div>

      {choice === 'yes' && (
        <div className="space-y-1.5">
          <label htmlFor="upload-custom-field-template" className="text-xs font-semibold text-foreground">
            Select custom fields <span className="text-destructive">*</span>
          </label>
          <select
            id="upload-custom-field-template"
            value={templateId ?? ''}
            disabled={disabled || loading}
            onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : null)}
            className={inputCls}
          >
            <option value="">{loading ? 'Loading templates…' : templates.length ? 'Select a template' : 'No templates yet'}</option>
            {templates.map((t: CustomFieldTemplate) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.fields.length} field{t.fields.length === 1 ? '' : 's'})
              </option>
            ))}
          </select>
          {!template && (
            <p className="text-[11px] text-muted-foreground">
              Pick a template to enable Download Template, or choose No.
            </p>
          )}
          {template && (
            <ul className="list-disc space-y-0.5 pl-5 text-[11px] text-muted-foreground">
              {template.fields.map((f) => (
                <li key={f.field_name}>
                  <span className="font-medium text-foreground">{customFieldHeader(f)}</span>: {csvFormatHint(f)}
                  {f.field_default !== null && f.field_default !== '' ? ' (blank uses the default for new contacts)' : ''}
                </li>
              ))}
              <li>A row with a missing required or invalid custom value is skipped. For an existing contact, blank custom cells keep the saved values.</li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
